import type { BattleSeat, BattleSummary } from "@loot-os/api-contract";

/** How one of the viewer's battles reads in a history row. */
export type BattleOutcome = "waiting" | "in-progress" | "won" | "lost";

/**
 * The viewer's seat in a battle summary, by end-user id (`GET /v1/me` carries it; the summary's
 * seats carry each player's). null when the viewer holds no seat, or the id is unknown.
 */
export function viewerSeat(
  battle: Pick<BattleSummary, "seats">,
  viewerEndUserId: string | null | undefined,
): BattleSeat | null {
  if (!viewerEndUserId) return null;
  return battle.seats.find((seat) => seat.endUser?.id === viewerEndUserId) ?? null;
}

/**
 * Waiting until the battle starts, in progress until it completes, then won or lost by the
 * viewer's seat. A completed battle whose seat the viewer cannot be matched to reads as lost
 * rather than won: `isWinner` is null only before completion.
 */
export function battleOutcome(
  battle: Pick<BattleSummary, "status">,
  seat: Pick<BattleSeat, "isWinner"> | null,
): BattleOutcome {
  if (battle.status === "created") return "waiting";
  if (battle.status === "started") return "in-progress";
  return seat?.isWinner ? "won" : "lost";
}

export const BATTLE_OUTCOME_LABEL: Record<BattleOutcome, string> = {
  waiting: "Waiting for players",
  "in-progress": "In progress",
  won: "Won",
  lost: "Lost",
};
